import { useState, useRef, useEffect } from "react";
import { Sparkles, Send, Loader2, RotateCcw, Globe, Users, ChevronRight } from "lucide-react";
import axios from "axios";
import { trackEvent } from "../../lib/track";
import SharedConstellationMap, { ORIENT } from "../common/SharedConstellationMap";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/* Extra keyframes for ConstellationTab hero/loading (not in shared component) */
const CT_EXTRA_STYLES_ID = "ct-extra-styles";
if (typeof document !== "undefined" && !document.getElementById(CT_EXTRA_STYLES_ID)) {
  const s = document.createElement("style");
  s.id = CT_EXTRA_STYLES_ID;
  s.textContent = `
    @keyframes cst-fade-up { 0% { transform: translateY(20px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }
    @keyframes cst-loading-orbit { 0% { transform: rotate(0deg) translateX(30px) rotate(0deg); } 100% { transform: rotate(360deg) translateX(30px) rotate(-360deg); } }
    @keyframes cst-suggestion-hover { 0%,100% { border-color: rgba(34,197,94,0.15); } 50% { border-color: rgba(34,197,94,0.4); } }
    @keyframes cst-ethics-flash { 0%,100% { border-color: rgba(239,68,68,0.3); box-shadow: 0 0 0px rgba(239,68,68,0); } 50% { border-color: rgba(239,68,68,0.8); box-shadow: 0 0 20px rgba(239,68,68,0.15); } }
  `;
  document.head.appendChild(s);
}

/* ══════════════════════════════════════════════════════════
   STEP CARDS
   ══════════════════════════════════════════════════════════ */
const StepCard = ({ step, index, isActive, onActivate }) => {
  const counts = {};
  step.apps.forEach(a => { const l = ORIENT[a.value_orientation]?.label || "Utility"; counts[l] = (counts[l] || 0) + 1; });
  return (
    <div onClick={onActivate} data-testid={`step-card-${index}`}
      className={`rounded-xl border p-4 cursor-pointer transition-all duration-300 ${
        isActive ? "bg-white/[0.06] border-emerald-500/40 shadow-lg shadow-emerald-500/[0.05]" : "bg-white/[0.02] border-white/[0.06] hover:bg-white/[0.04]"
      }`}>
      <div className="flex items-start gap-3 mb-2">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${isActive ? "bg-emerald-500/20" : "bg-white/[0.05]"}`}>
          <span className={`text-sm font-bold ${isActive ? "text-emerald-400" : "text-slate-400"}`}>{index + 1}</span>
        </div>
        <div className="flex-1 min-w-0">
          <h3 className={`text-sm font-semibold leading-snug ${isActive ? "text-white" : "text-slate-300"}`}>{step.name}</h3>
          <p className="text-xs text-slate-400 mt-0.5">{step.description}</p>
        </div>
        <div className="flex gap-1.5 flex-shrink-0">
          {Object.entries(counts).map(([label, count]) => {
            const o = Object.values(ORIENT).find(v => v.label === label) || ORIENT.utility;
            return <span key={label} className="text-[10px] font-semibold px-2 py-0.5 rounded-full border" style={{ color: o.fill, borderColor: `${o.fill}33`, background: `${o.fill}11` }}>{count} {label}</span>;
          })}
        </div>
      </div>
      {isActive && (
        <div className="space-y-1.5 pt-2 border-t border-white/[0.06]">
          {step.apps.map((app, i) => {
            const o = ORIENT[app.value_orientation] || ORIENT.utility;
            return (
              <div key={i} className="flex items-start gap-2 py-1">
                <span className="w-2 h-2 rounded-full mt-1 flex-shrink-0" style={{ background: o.fill }} />
                <div className="flex-1 min-w-0">
                  <span className="text-xs font-medium text-white">{app.product_name}</span>
                  {app.tagline && <p className="text-[10px] text-slate-400 line-clamp-1">{app.tagline}</p>}
                  <div className="flex gap-2 mt-0.5">
                    {app.country && <span className="text-[10px] text-slate-500 flex items-center gap-0.5"><Globe className="w-2.5 h-2.5" />{app.country}</span>}
                    {app.founder_composition && app.founder_composition !== "unknown" && <span className="text-[10px] text-slate-500 flex items-center gap-0.5"><Users className="w-2.5 h-2.5" />{app.founder_composition.replace("_"," ")}</span>}
                  </div>
                </div>
              </div>
            );
          })}
          {/* Research Papers */}
          {step.research_papers?.length > 0 && (
            <div className="mt-2 pt-2 border-t border-purple-500/10">
              <p className="text-[9px] text-purple-400 uppercase tracking-wider font-bold mb-1.5">Related Research</p>
              {step.research_papers.map((paper, i) => (
                <div key={`p-${i}`} className="flex items-start gap-2 py-1">
                  <span className="w-2 h-2 rounded-full mt-1 flex-shrink-0 bg-purple-400/60" />
                  <div className="flex-1 min-w-0">
                    <span className="text-[11px] text-purple-200 line-clamp-1">{paper.paper_title}</span>
                    <div className="flex gap-2 mt-0.5">
                      {paper.country && <span className="text-[9px] text-slate-500">{paper.country}</span>}
                      <span className="text-[9px] text-slate-500">{paper.citations || 0} cites</span>
                      <span className="text-[9px] text-slate-500 capitalize">{paper.source}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
      {!isActive && <div className="text-[10px] text-slate-500 flex items-center gap-1"><ChevronRight className="w-3 h-3" />{step.apps.length} apps{step.research_papers?.length > 0 && ` + ${step.research_papers.length} papers`}</div>}
    </div>
  );
};

/* ══════════════════════════════════════════════════════════
   MAIN TAB
   ══════════════════════════════════════════════════════════ */
export const ConstellationTab = () => {
  const [idea, setIdea] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activeStep, setActiveStep] = useState(null);
  const [entityCount, setEntityCount] = useState("90,000+");
  const inputRef = useRef(null);
  const cardsRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/pulse`).then(r => {
      if (r.data?.total_entities) setEntityCount(r.data.total_entities.toLocaleString() + "+");
    }).catch(() => {});
  }, []);

  const handleSubmit = async () => {
    if (!idea.trim() || loading) return;
    setLoading(true); setError(null); setResult(null); setActiveStep(null);
    trackEvent("constellation_query", "constellation", idea.trim().slice(0, 100));
    try {
      const res = await axios.post(`${API}/constellation`, { idea: idea.trim() });
      if (res.data.error) setError(res.data.error);
      else setResult(res.data);
    } catch { setError("Failed to generate constellation. Please try again."); }
    finally { setLoading(false); }
  };

  const handleReset = () => { setIdea(""); setResult(null); setError(null); setActiveStep(null); inputRef.current?.focus(); };

  useEffect(() => {
    if (activeStep !== null && cardsRef.current?.children[activeStep])
      cardsRef.current.children[activeStep].scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
  }, [activeStep]);

  const [typedIdx, setTypedIdx] = useState(0);
  const prompts = ["an AI therapist for underserved communities", "a smart farming assistant", "an accessibility tool for the visually impaired", "a bias detection system for hiring"];
  useEffect(() => {
    if (result || loading) return;
    const t = setInterval(() => setTypedIdx(i => (i + 1) % prompts.length), 4000);
    return () => clearInterval(t);
  }, [result, loading]);

  return (
    <div className="animate-fade-in" data-testid="constellation-tab">
      {/* Hero Header */}
      <div className="relative overflow-hidden rounded-2xl mb-6" style={{ background: "linear-gradient(135deg, #060a12 0%, #0c1a2e 50%, #0a1628 100%)", minHeight: result ? 0 : 340, padding: result ? "24px" : "48px 32px" }}>
        {!result && (
          <div style={{ position: "absolute", inset: 0, overflow: "hidden", pointerEvents: "none" }}>
            {Array.from({ length: 20 }, (_, i) => (
              <div key={i} style={{
                position: "absolute",
                left: `${10 + (i * 37) % 80}%`, top: `${10 + (i * 53) % 80}%`,
                width: 4 + (i % 3) * 2, height: 4 + (i % 3) * 2, borderRadius: "50%",
                background: i % 4 === 0 ? "#34d399" : i % 4 === 1 ? "#60a5fa" : i % 4 === 2 ? "#a78bfa" : "#f87171",
                opacity: 0.15 + (i % 5) * 0.05,
                animation: `cst-float ${3 + (i % 4)}s ease-in-out ${-(i * 0.5)}s infinite`,
              }} />
            ))}
          </div>
        )}

        <div className="relative z-10">
          <div className={`text-center ${result ? "mb-4" : "mb-8"}`}>
            <div className="flex items-center justify-center gap-2.5 mb-2">
              <div className="w-8 h-8 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <Sparkles className="w-4.5 h-4.5 text-emerald-400" />
              </div>
              <h2 className="text-2xl font-bold tracking-tight text-white">Constellation</h2>
            </div>
            <p className="text-xs font-bold text-emerald-400 uppercase tracking-[0.25em]">Map your Idea Orbit now!</p>
            {!result && !loading && (
              <p className="text-sm text-slate-400 mt-3 max-w-lg mx-auto leading-relaxed" style={{ animation: "cst-fade-up 0.6s ease-out" }}>
                Describe what you want to build. We'll map the AI tools, apps, devices, and ecosystem connections from <span className="text-emerald-400 font-semibold">{entityCount}</span> real products.
              </p>
            )}
          </div>

          <div className="max-w-2xl mx-auto">
            {!result && !loading && !idea && (
              <div className="text-center mb-4" style={{ animation: "cst-fade-up 0.8s ease-out 0.2s both" }}>
                <p className="text-xs text-slate-500 mb-1">e.g. I want to build...</p>
                <p className="text-base text-slate-400 font-medium italic" key={typedIdx} style={{ animation: "cst-fade-up 0.4s ease-out" }}>
                  "{prompts[typedIdx]}"
                </p>
              </div>
            )}

            <div className="relative" style={{ animation: result ? "none" : "cst-fade-up 0.6s ease-out 0.3s both" }}>
              <textarea ref={inputRef} value={idea} onChange={(e) => setIdea(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); handleSubmit(); } }}
                placeholder="What do you want to build?"
                rows={1} className="w-full px-5 py-4 bg-white/[0.06] border border-white/[0.12] rounded-2xl text-sm resize-none focus:outline-none focus:border-emerald-500/50 focus:bg-white/[0.08] placeholder:text-slate-400 transition-all pr-36 text-white"
                style={{ minHeight: 52, maxHeight: 100 }}
                data-testid="constellation-input" />
              <div className="absolute top-1/2 -translate-y-1/2 right-3 flex items-center gap-1.5">
                {result && <button onClick={handleReset} className="p-2 rounded-xl hover:bg-white/[0.08] text-slate-400 transition-colors" data-testid="constellation-reset"><RotateCcw className="w-4 h-4" /></button>}
                <button onClick={handleSubmit} disabled={!idea.trim() || loading}
                  className="flex items-center gap-2 px-5 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-30 text-white text-sm font-bold rounded-xl transition-all shadow-lg shadow-emerald-500/25"
                  data-testid="constellation-submit">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                  {loading ? "Mapping..." : "Discover"}
                </button>
              </div>
            </div>

            {!result && !loading && (
              <div className="flex flex-wrap justify-center gap-2 mt-5" style={{ animation: "cst-fade-up 0.6s ease-out 0.5s both" }}>
                {["AI mental health support for students", "Smart agriculture monitoring", "Content creation pipeline"].map((ex, i) => (
                  <button key={ex} onClick={() => { setIdea(ex); inputRef.current?.focus(); }}
                    className="text-xs px-4 py-2 bg-white/[0.04] border border-emerald-500/15 rounded-full hover:bg-emerald-500/[0.1] hover:border-emerald-500/40 transition-all text-slate-400 hover:text-emerald-300 font-medium"
                    style={{ animation: `cst-suggestion-hover 4s ease-in-out ${i * 1.3}s infinite` }}
                    data-testid="constellation-example">{ex}</button>
                ))}
              </div>
            )}
          </div>

          {result && (
            <div className="text-center mt-2">
              <p className="text-sm text-slate-400">Mapped <span className="text-emerald-400 font-bold">{result.total_apps_matched}</span> apps across <span className="text-emerald-400 font-bold">{result.steps.length}</span> steps</p>
            </div>
          )}
        </div>
      </div>

      {error && <div className="max-w-2xl mx-auto text-center text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-xl py-3 mb-4" data-testid="constellation-error">{error}</div>}

      {loading && !result && (
        <div className="text-center py-12">
          <div className="relative w-20 h-20 mx-auto mb-5">
            <div className="absolute inset-0 rounded-full border-2 border-emerald-500/20" />
            <div className="absolute inset-2 rounded-full border border-emerald-500/10" />
            <div style={{ position: "absolute", width: 8, height: 8, borderRadius: "50%", background: "#34d399", top: "50%", left: "50%", marginTop: -4, marginLeft: -4, animation: "cst-loading-orbit 1.5s linear infinite", boxShadow: "0 0 12px #34d399" }} />
            <div style={{ position: "absolute", width: 6, height: 6, borderRadius: "50%", background: "#60a5fa", top: "50%", left: "50%", marginTop: -3, marginLeft: -3, animation: "cst-loading-orbit 2.2s linear -0.5s infinite", boxShadow: "0 0 10px #60a5fa" }} />
            <div style={{ position: "absolute", width: 5, height: 5, borderRadius: "50%", background: "#a78bfa", top: "50%", left: "50%", marginTop: -2.5, marginLeft: -2.5, animation: "cst-loading-orbit 3s linear -1s infinite", boxShadow: "0 0 8px #a78bfa" }} />
          </div>
          <p className="text-sm text-slate-300 font-medium">Mapping your idea orbit...</p>
          <p className="text-xs text-slate-500 mt-1">Analyzing {entityCount} entities to find your ecosystem</p>
        </div>
      )}

      {result && (
        <div className="space-y-5">
          {result.ethics_flag && (
            <div data-testid="ethics-flag" className="max-w-2xl mx-auto rounded-xl px-5 py-4 bg-red-500/[0.06]"
              style={{ border: "1px solid rgba(239,68,68,0.3)", animation: "cst-ethics-flash 2s ease-in-out infinite" }}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-red-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-red-400 font-bold text-sm">!</span>
                </div>
                <div>
                  <p className="text-sm font-semibold text-red-400">Conscious Innovation Alert</p>
                  <p className="text-xs text-slate-300 mt-1 leading-relaxed">{result.ethics_message}</p>
                </div>
              </div>
            </div>
          )}

          <SharedConstellationMap data={result} activeStep={activeStep} setActiveStep={setActiveStep} />

          {/* Orientation legend */}
          <div className="flex justify-center gap-4 flex-wrap">
            {Object.entries(ORIENT).map(([k, v]) => (
              <span key={k} className="flex items-center gap-2 text-xs text-slate-400">
                <span className="w-3 h-3 rounded-full" style={{ background: v.fill, boxShadow: `0 0 6px ${v.fill}50` }} />{v.label}
              </span>
            ))}
            <span className="flex items-center gap-2 text-xs text-slate-400">
              <span className="w-3 h-3 rounded-full bg-slate-500" />Software
            </span>
            <span className="flex items-center gap-2 text-xs text-slate-400">
              <span className="w-3 h-3 rounded-sm bg-slate-500" />Hardware
            </span>
          </div>

          {/* Workflow Steps */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <h3 className="text-xs font-bold text-slate-400 uppercase tracking-widest">Workflow Steps</h3>
              <div className="flex-1 h-px bg-gradient-to-r from-emerald-500/20 to-transparent" />
              <p className="text-[10px] text-slate-500">Click a step node above to highlight its card</p>
            </div>
            <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {result.steps.map((step, i) => (
                <StepCard key={i} step={step} index={i} isActive={activeStep === i} onActivate={() => setActiveStep(activeStep === i ? null : i)} />
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConstellationTab;
